/*
 * @Description:
 * @Author: Luke Z
 * @Date: 2021-06-15 12:29:04
 * @LastEditors: Luke Z
 * @LastEditTime: 2021-06-15 12:30:49
 * @FilePath: /unifyxp-v2-store-portal/src/tools/hooks/useItemChangeWithOrder/index.ts
 */
import React from 'react';
import _ from 'lodash';
interface MappedSelectionValue {
  current: boolean;
  checked: boolean;
  product: any;
}

const generateMappedSelectedKeys = (selectedItems = []) => {
  return selectedItems.reduce((pre, now) => {
    return {
      ...pre,
      [now.uuid]: {
        current: false,
        checked: true,
        product: now,
      },
    };
  }, {});
};

const onOkClick = (
  mappedSelectedKeys: { [key: string]: MappedSelectionValue } | null,
  onOk,
  onCancel,
) => {
  const selectedProducts = Object.values(mappedSelectedKeys || {}).reduce(
    (pre: any, now: MappedSelectionValue) => {
      if (now.checked) {
        return [...pre, now.product];
      } else {
        return pre;
      }
    },
    [],
  );

  onOk(selectedProducts);
  onCancel();
};

const fullSelectedRowKeysData = (
  mappedSelectedKeys: { [key: string]: MappedSelectionValue } | null,
) => {
  return Object.values(mappedSelectedKeys || {})
    .filter((item) => item.checked)
    .map((item) => ({ uuid: item.product.uuid, product: item.product }));
};

const useProductsHasUpdate = (
  products,
  setMappedSelectedKeys,
  setSelectedRowKeys,
  mappedSelectedKeys: { [key: string]: MappedSelectionValue } | null,
  extraProps?,
) => {
  const { noPagination, keepData = true } = extraProps || {};

  React.useEffect(() => {
    const currentMappedKeys = mappedSelectedKeys;
    const nextMappedKeys = _.cloneDeep(currentMappedKeys || {}) as {
      [key: string]: MappedSelectionValue;
    };

    // 第一遍处理: 遍历当前页的产品 (`products`)
    (products || []).forEach((product) => {
      const { uuid } = product;
      // 检查该产品是否已存在于总体选中映射中
      // 如果存在，标记它在当前页面 (`current: true`)
      if (nextMappedKeys[uuid]) {
        nextMappedKeys[uuid].current = true;
      } else {
        nextMappedKeys[uuid] = {
          checked: false,
          current: true,
          product: product,
        };
      }
    });

    /** 第二遍处理: 遍历更新后的映射 (`nextMappedKeys`)
     * 检查映射中的每个条目是否真的存在于当前页的 `products` 列表中
     * 如果条目不在当前页的 `products` 列表中
     * 并且该条目存在于映射中则将其 `current` 标志设置为 `false`
     */
    Object.keys(nextMappedKeys).forEach((uuid) => {
      const productExistsOnCurrentPage =
        products && products.some((p) => p.uuid === uuid);
      if (!productExistsOnCurrentPage) {
        if (nextMappedKeys[uuid]) {
          nextMappedKeys[uuid].current = false;
        }
      }
    });

    setSelectedRowKeys(fullSelectedRowKeysData(nextMappedKeys));

    if (noPagination && !keepData) {
      setMappedSelectedKeys({});
    } else if (!_.isEqual(currentMappedKeys, nextMappedKeys)) {
      setMappedSelectedKeys(nextMappedKeys);
    }
  }, [
    products,
    mappedSelectedKeys,
    setMappedSelectedKeys,
    setSelectedRowKeys,
    noPagination,
    keepData,
  ]);
};

export const useItemsChange = (
  selectedItems, //原来选择的
  products, //当前页面的所有数据
  onOk,
  onCancel,
  extraProps?,
) => {
  const { noPagination, keepData } = extraProps || {};

  // Use the interface for state type annotation
  const [mappedSelectedKeys, setMappedSelectedKeys] = React.useState<{
    [key: string]: MappedSelectionValue;
  } | null>(generateMappedSelectedKeys(selectedItems));
  const [selectedRowKeys, setSelectedRowKeys] = React.useState<any>([]);

  useProductsHasUpdate(
    products,
    setMappedSelectedKeys,
    setSelectedRowKeys,
    mappedSelectedKeys,
    { noPagination, keepData },
  );

  const onOkPress = () => {
    onOkClick(mappedSelectedKeys, onOk, onCancel);
  };

  const _onSelectChange = (selections: string[]) => {
    const currentSelectionsSet = new Set(selections);

    // 计算 mappedSelectedKeys 的下一个状态
    const prevMappedKeys = mappedSelectedKeys;
    const nextMappedKeys = { ...(prevMappedKeys || {}) } as {
      [key: string]: MappedSelectionValue;
    };

    (products || []).forEach((product) => {
      const { uuid } = product;
      const prevProductState = prevMappedKeys
        ? prevMappedKeys[uuid]
        : undefined;
      const isChecked = currentSelectionsSet.has(uuid);

      if (
        !prevProductState ||
        prevProductState.checked !== isChecked ||
        !prevProductState.current
      ) {
        nextMappedKeys[uuid] = {
          ...(prevProductState || { product: product }),
          checked: isChecked,
          current: true,
        };
      } else if (prevProductState && !prevProductState.current) {
        nextMappedKeys[uuid] = {
          ...prevProductState,
          current: true,
        };
      }
    });

    // 顺序调用 setState 更新状态
    setMappedSelectedKeys(nextMappedKeys);
    setSelectedRowKeys(fullSelectedRowKeysData(nextMappedKeys));
  };

  const clearMapKeys = () => {
    setMappedSelectedKeys({});
    setSelectedRowKeys([]);
  };

  return {
    onOk: onOkPress,
    onSelectChange: _onSelectChange,
    selectedRowKeys,
    setSelectedRowKeys,
    clearMapKeys,
  };
};
